import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Text,
  Button,
  Group,
  Badge,
  Tabs,
  LoadingOverlay,
  Stack,
  Grid,
  ThemeIcon,
  Avatar,
  Divider,
  TextInput,
  Select,
  Switch,
  SimpleGrid,
  Card,
  PasswordInput,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconUser,
  IconShoppingBag,
  IconRocket,
  IconTicket,
  IconCheck,
  IconX,
  IconDeviceFloppy,
  IconChartBar,
} from "@tabler/icons-react";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import { API_BASE_URL } from "../../config/baseUrls";

// --- API HELPERS ---
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  "Content-Type": "application/json",
});

const BASE_URL = `${API_BASE_URL}/users`;

// =============================================================================
// TAB 1: OVERVIEW (Stats)
// =============================================================================
const OverviewTab = ({ user }) => {
  const counts = user._count || {};

  // Check if the array exists and has items
  const shops = user.shopFronts || (user.shopFront ? [user.shopFront] : []);

  const StatCard = ({ label, value, icon, color }) => (
    <Card withBorder radius="md" p="md">
      <Group justify="space-between">
        <div>
          <Text size="xs" c="dimmed" fw={700} tt="uppercase">
            {label}
          </Text>
          <Text fw={700} size="xl">
            {value}
          </Text>
        </div>
        <ThemeIcon variant="light" color={color} size="lg" radius="md">
          {icon}
        </ThemeIcon>
      </Group>
    </Card>
  );

  return (
    <Stack gap="lg">
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
        <StatCard
          label="Orders"
          value={counts.productOrders || 0}
          icon={<IconShoppingBag size={20} />}
          color="blue"
        />
        <StatCard
          label="Projects"
          value={counts.Project || 0}
          icon={<IconRocket size={20} />}
          color="orange"
        />
        <StatCard
          label="Votes Cast"
          value={counts.projectVotes || 0}
          icon={<IconCheck size={20} />}
          color="green"
        />
        <StatCard
          label="Tickets"
          value={counts.tickets || 0}
          icon={<IconTicket size={20} />}
          color="purple"
        />
      </SimpleGrid>

      {/* UPDATED: Loop through multiple shops */}
      {shops.length > 0 && (
        <Box>
          <Text fw={700} size="lg" mb="sm">
            Owned Shops ({shops.length})
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            {shops.map((shop) => (
              <Paper
                key={shop.id}
                p="md"
                withBorder
                radius="md"
                bg="blue.0"
                style={{ borderColor: "var(--mantine-color-blue-2)" }}
              >
                <Group>
                  <Avatar size="lg" radius="md" src={shop.logoUrl} color="blue">
                    {shop.name ? shop.name[0] : "S"}
                  </Avatar>
                  <div>
                    <Text size="xs" fw={700} tt="uppercase" c="blue.8">
                      Shopfront
                    </Text>
                    <Text fw={700} size="md" lineClamp={1}>
                      {shop.name}
                    </Text>
                    <Badge size="xs" color={shop.isActive ? "green" : "red"}>
                      {shop.isActive ? "Active" : "Suspended"}
                    </Badge>
                  </div>
                </Group>
              </Paper>
            ))}
          </SimpleGrid>
        </Box>
      )}
    </Stack>
  );
};

// =============================================================================
// TAB 2: PROFILE SETTINGS (Edit)
// =============================================================================
const SettingsTab = ({ user, onRefresh }) => {
  const [formData, setFormData] = useState({
    nickname: user.nickname || "",
    role: user.role || "USER",
    isEmailVerified: user.isEmailVerified || false,
    password: "", // Only send if changing
  });
  const [loading, setLoading] = useState(false);
  const { showSuccess, showError } = useNotification();

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = { ...formData };
      if (!payload.password) delete payload.password; // Don't send empty pass

      const res = await fetch(`${BASE_URL}/${user.cuid}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (data.success) {
        showSuccess("Saved", "User profile updated");
        onRefresh();
      } else showError("Error", data.error);
    } catch (e) {
      showError("Error", "Update failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack maxW={600}>
      <TextInput label="Email (Read Only)" value={user.email} disabled />
      <TextInput
        label="Nickname"
        value={formData.nickname}
        onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
      />
      <Select
        label="System Role"
        data={["USER", "SHOP_OWNER", "MODERATOR", "ADMIN", "SUPER_ADMIN"]}
        value={formData.role}
        onChange={(v) => setFormData({ ...formData, role: v })}
      />
      <PasswordInput
        label="New Password"
        placeholder="Leave blank to keep current"
        value={formData.password}
        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
      />

      <Paper p="md" withBorder radius="md" mt="sm">
        <Group justify="space-between">
          <Box>
            <Text fw={500} size="sm">
              Email Verification
            </Text>
            <Text size="xs" c="dimmed">
              Manually verify or unverify this user.
            </Text>
          </Box>
          <Switch
            checked={formData.isEmailVerified}
            onChange={(e) =>
              setFormData({
                ...formData,
                isEmailVerified: e.currentTarget.checked,
              })
            }
            color="green"
            size="md"
            thumbIcon={
              formData.isEmailVerified ? (
                <IconCheck size={12} />
              ) : (
                <IconX size={12} />
              )
            }
          />
        </Group>
      </Paper>

      <Group justify="flex-end" mt="md">
        <Button
          leftSection={<IconDeviceFloppy size={18} />}
          onClick={handleSave}
          loading={loading}
        >
          Save Changes
        </Button>
      </Group>
    </Stack>
  );
};

// =============================================================================
// MAIN WORKSPACE
// =============================================================================
export default function UserWorkspace() {
  const { id } = useParams(); // Note: route should use :id, backend uses :userId
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const { showError } = useNotification();

  const fetchUser = async () => {
    try {
      const res = await fetch(`${BASE_URL}/${id}`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success) setUser(data.data);
      else {
        showError("Error", "User not found");
        navigate("/admin/dashboard/users");
      }
    } catch (e) {
      showError("Error", "Failed to load user");
      navigate("/admin/dashboard/users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [id]);

  if (loading) return <LoadingOverlay visible />;
  if (!user) return <Text>User not found</Text>;

  return (
    <Box>
      {/* HEADER */}
      <Group mb="lg">
        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconArrowLeft size={18} />}
          onClick={() => navigate("/admin/dashboard/users")}
        >
          Back to List
        </Button>
        <Divider orientation="vertical" />
        <Group>
          <Avatar src={user.profileImage} radius="xl" color="blue" size="md">
            {user.nickname?.charAt(0)}
          </Avatar>
          <Stack gap={0}>
            <Text fw={700} size="lg">
              {user.nickname}
            </Text>
            <Group gap={6}>
              <Text size="xs" c="dimmed">
                {user.email}
              </Text>
              <Badge size="xs" variant="light">
                {user.role}
              </Badge>
            </Group>
          </Stack>
        </Group>
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab} radius="md">
        <Tabs.List mb="md">
          <Tabs.Tab value="overview" leftSection={<IconChartBar size={16} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="settings" leftSection={<IconUser size={16} />}>
            Profile & Settings
          </Tabs.Tab>
        </Tabs.List>

        <Paper p="xl" radius="md" withBorder mih={400} pos="relative">
          <Tabs.Panel value="overview">
            <OverviewTab user={user} />
          </Tabs.Panel>
          <Tabs.Panel value="settings">
            <SettingsTab user={user} onRefresh={fetchUser} />
          </Tabs.Panel>
        </Paper>
      </Tabs>
    </Box>
  );
}
